import React from "react";
import { cn } from "@/lib/utils";
import { STATUS_META, statusMeta, STREAM_COLORS, STREAM_SHORT } from "@/lib/format";
import { Check, TriangleAlert, OctagonAlert, Minus, TrendingUp, TrendingDown } from "lucide-react";

/* -------- SampleDataTag (per-pane demo-data marker) -------- */
export const SampleDataTag = ({ className }) => (
  <span
    data-testid="sample-data-tag"
    className={cn("inline-flex items-center gap-1 rounded-md bg-cnp-amber/15 text-[#8A5A00] text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 whitespace-nowrap", className)}
    title="Illustrative sample data"
  >
    <span className="h-1.5 w-1.5 rounded-full bg-cnp-amber" /> Sample Data
  </span>
);

/* -------- SectionCard -------- */
export const SectionCard = ({ title, subtitle, action, children, className, testid, sample = true }) => (
  <div
    data-testid={testid}
    className={cn("bg-cnp-card rounded-lg border border-cnp-border shadow-sm p-5 animate-fade-in-up", className)}
  >
    {(title || action || sample) && (
      <div className="flex items-start justify-between mb-4 gap-3">
        <div>
          {title && (
            <h3 className="text-[13px] uppercase tracking-wider font-semibold text-cnp-blue">{title}</h3>
          )}
          {subtitle && <p className="text-xs text-cnp-textMuted mt-0.5">{subtitle}</p>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {sample && <SampleDataTag />}
          {action}
        </div>
      </div>
    )}
    {children}
  </div>
);

/* -------- StatusChip (glyph + text, never colour alone) -------- */
const GLYPH = { READY: Check, AT_RISK: TriangleAlert, NOT_READY: OctagonAlert, NA: Minus };
export const StatusChip = ({ status, size = "sm", testid }) => {
  const m = statusMeta(status);
  const Icon = GLYPH[status] || Minus;
  return (
    <span
      data-testid={testid}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full font-semibold whitespace-nowrap",
        size === "lg" ? "px-3 py-1.5 text-sm" : "px-2.5 py-1 text-xs"
      )}
      style={{ backgroundColor: m.bg, color: m.text }}
    >
      <Icon size={size === "lg" ? 15 : 12} strokeWidth={2.5} />
      {m.label}
    </span>
  );
};

/* -------- TrendArrow -------- */
export const TrendArrow = ({ delta }) => {
  if (delta == null || delta === 0)
    return <span className="inline-flex items-center text-cnp-textMuted"><Minus size={14} /></span>;
  const up = delta > 0;
  return (
    <span className={cn("inline-flex items-center gap-0.5 text-xs font-semibold", up ? "text-cnp-green" : "text-cnp-red")}>
      {up ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
      {up ? "+" : ""}{delta}%
    </span>
  );
};

/* -------- DataCoverageBadge -------- */
export const DataCoverageBadge = ({ pct, testid }) => (
  <span
    data-testid={testid}
    className="inline-flex items-center gap-1 rounded-md bg-cnp-bg border border-cnp-border px-1.5 py-0.5 text-[10px] font-medium text-cnp-textMuted"
    title="Fraction of scoring inputs actually present"
  >
    <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: pct >= 70 ? "#00A651" : pct >= 40 ? "#F5A623" : "#D0021B" }} />
    {pct}% data
  </span>
);

/* -------- Sparkline -------- */
export const Sparkline = ({ data = [], color = "#0075C9", w = 64, h = 24 }) => {
  if (!data.length) return null;
  const vals = data.map((d) => (typeof d === "number" ? d : d.value));
  const min = Math.min(...vals), max = Math.max(...vals);
  const range = max - min || 1;
  const pts = vals
    .map((v, i) => `${(i / (vals.length - 1)) * w},${h - ((v - min) / range) * (h - 4) - 2}`)
    .join(" ");
  return (
    <svg width={w} height={h} className="overflow-visible">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.75" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
};

/* -------- MiniGauge (small radial) -------- */
export const MiniGauge = ({ value = 0, size = 56, color }) => {
  const r = (size - 8) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, value)) / 100;
  const col = color || (value >= 90 ? "#00A651" : value >= 70 ? "#F5A623" : "#D0021B");
  return (
    <svg width={size} height={size} className="-rotate-90">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#E3E8EF" strokeWidth="5" />
      <circle
        cx={size / 2} cy={size / 2} r={r} fill="none" stroke={col} strokeWidth="5"
        strokeDasharray={c} strokeDashoffset={c * (1 - pct)} strokeLinecap="round"
        style={{ transition: "stroke-dashoffset 0.6s ease" }}
      />
      <text x="50%" y="50%" dominantBaseline="central" textAnchor="middle"
        className="rotate-90 tnum" style={{ transformOrigin: "center", fontSize: size * 0.26, fontWeight: 700, fill: "#1F2937" }}>
        {Math.round(value)}
      </text>
    </svg>
  );
};

/* -------- KpiCard -------- */
export const KpiCard = ({ label, value, sub, accent = "#0075C9", trend, onClick, testid, children }) => (
  <div
    data-testid={testid}
    onClick={onClick}
    className={cn(
      "bg-cnp-card rounded-lg border border-cnp-border shadow-sm p-4 flex flex-col justify-between min-h-[104px]",
      onClick && "cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
    )}
  >
    <div className="flex items-start justify-between">
      <span className="text-[11px] uppercase tracking-wide text-cnp-textMuted font-semibold leading-tight">{label}</span>
      {trend != null && <TrendArrow delta={trend} />}
    </div>
    <div className="flex items-end justify-between mt-1">
      <div>
        <div className="text-[32px] leading-none font-semibold tnum" style={{ color: accent }}>{value}</div>
        {sub && <div className="text-[11px] text-cnp-textMuted mt-1">{sub}</div>}
      </div>
      {children}
    </div>
  </div>
);

/* -------- DriverBar -------- */
export const DriverBar = ({ label, value, weight, definition }) => {
  const col = value >= 90 ? "#00A651" : value >= 70 ? "#F5A623" : "#D0021B";
  return (
    <div className="group py-1.5" title={definition || (weight ? `Weight ${weight}%` : "")}>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-cnp-text font-medium">{label}{weight ? <span className="ml-1.5 text-[10px] font-semibold text-cnp-blue bg-cnp-blue/10 rounded px-1 py-0.5 align-middle">{weight}% of TRI</span> : null}</span>
        <span className="tnum font-semibold text-cnp-text">{Math.round(value)}%</span>
      </div>
      <div className="h-2 rounded-full bg-cnp-bg overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.max(2, value)}%`, backgroundColor: col }} />
      </div>
      {definition && <p className="text-[10px] text-cnp-textMuted mt-1">{definition}</p>}
    </div>
  );
};

/* -------- StreamBadges (multi-stream overlap indicator) -------- */
export const StreamBadges = ({ streams = [], only, showMultiFlag = false, className, testid }) => {
  const list = only ? streams.filter((s) => s !== only) : streams;
  if (!list.length) return null;
  const isMulti = streams.length > 1;
  return (
    <span data-testid={testid} className={cn("inline-flex flex-wrap items-center gap-1 align-middle", className)}>
      {showMultiFlag && isMulti && (
        <span
          className="inline-flex items-center rounded-md bg-cnp-navy/10 text-cnp-navy px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide leading-none"
          title="Serves more than one stream"
        >
          Multi
        </span>
      )}
      {list.map((s) => (
        <span
          key={s}
          className="inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-semibold leading-none whitespace-nowrap"
          style={{ backgroundColor: `${STREAM_COLORS[s] || "#475569"}1A`, color: STREAM_COLORS[s] || "#475569" }}
        >
          {STREAM_SHORT[s] || s}
        </span>
      ))}
    </span>
  );
};

/* -------- EmptyState -------- */
export const EmptyState = ({ icon: Icon, title, hint }) => (
  <div className="flex flex-col items-center justify-center py-10 text-center">
    {Icon && <Icon className="text-cnp-grey mb-3" size={32} />}
    <p className="text-sm font-medium text-cnp-text">{title}</p>
    {hint && <p className="text-xs text-cnp-textMuted mt-1 max-w-xs">{hint}</p>}
  </div>
);
